import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '../../base/Text';
import { Input } from '../../base/Input';
import { Button } from '../../base/Button';
import { useAuth } from '../../../hooks/useAuth';
import styles from './AuthModal.module.css';

type AuthModalState = 'idle' | 'loading' | 'success' | 'error';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const AuthModal = ({ isOpen, onClose, onSuccess }: AuthModalProps) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [state, setState] = useState<AuthModalState>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const { signInWithEmail, signInWithGoogle } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState('loading');
    setErrorMessage('');
    const { error } = await signInWithEmail(email.trim());
    if (error) {
      setState('error');
      setErrorMessage(error);
    } else {
      setState('success');
      onSuccess?.();
    }
  };

  const handleGoogleSignIn = async () => {
    setState('loading');
    setErrorMessage('');
    const { error } = await signInWithGoogle();
    if (error) {
      setState('error');
      setErrorMessage(error);
    }
  };

  const handleClose = () => {
    setEmail('');
    setState('idle');
    setErrorMessage('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {state === 'success' ? (
          <div className={styles.successState}>
            <span className={styles.successIcon}>✉️</span>
            <Text variant="title" as="h2" className={styles.title}>
              {t('auth.checkYourEmail')}
            </Text>
            <Text variant="body" muted className={styles.description}>
              <span dangerouslySetInnerHTML={{ __html: t('auth.magicLinkSent', { email }) }} />
            </Text>
            <Button variant="ghost" onClick={handleClose} fullWidth>
              {t('auth.close')}
            </Button>
          </div>
        ) : (
          <>
            <Text variant="title" as="h2" className={styles.title}>
              {t('auth.syncYourLinks')}
            </Text>
            <Text variant="body" muted className={styles.description}>
              {t('auth.signInToAccess')}
            </Text>
            <div className={styles.socialButtons}>
              <Button
                type="button"
                variant="primary"
                onClick={handleGoogleSignIn}
                disabled={state === 'loading'}
                fullWidth
              >
                <svg viewBox="0 0 24 24" width="18" height="18" style={{ marginRight: '8px' }}>
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('auth.continueWithGoogle')}
              </Button>
            </div>
            <div className={styles.divider}><span>{t('auth.or')}</span></div>
            <form onSubmit={handleSubmit} className={styles.form}>
              <Text variant="meta" muted style={{ marginBottom: '8px' }}>
                {t('auth.orSignInWithEmail')}
              </Text>
              <Input
                type="email"
                placeholder={t('auth.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={state === 'loading'}
                fullWidth
                autoFocus
              />
              {state === 'error' && errorMessage && (
                <Text variant="meta" className={styles.error}>{errorMessage}</Text>
              )}
              <div className={styles.actions}>
                <Button type="button" variant="ghost" onClick={handleClose} disabled={state === 'loading'}>
                  {t('auth.cancel')}
                </Button>
                <Button type="submit" variant="primary" disabled={!email.trim() || state === 'loading'}>
                  {state === 'loading' ? t('auth.sending') : t('auth.sendMagicLink')}
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
